import { Flex, Image, SimpleGrid, Skeleton } from "@mantine/core";
import { JSX } from "react";
import styled from "styled-components";
import { useIsMediumScreen } from "../../hooks/breakpoints";

const CampgroundImage: typeof Image = styled(Image)`
  height: 100%;
  width: 100%;
  contain: size;
  object-fit: cover;
  cursor: pointer;
  border-radius: var(--mantine-radius-md);

  &:hover {
    filter: brightness(0.8);
    transition: filter 0.2s ease;
  }
`;

const CampgroundImageSkeleton: typeof Skeleton = styled(Skeleton)`
  height: 100%;
  width: 100%;
  border-radius: var(--mantine-radius-md);
`;

interface CampgroundImageProps {
  images: string[];
  openImage: (index: number) => void;
  loading?: boolean;
}

export const CampgroundImageLayout = ({
  images,
  openImage,
  loading,
}: CampgroundImageProps) => {
  const isMediumScreen = useIsMediumScreen();

  if (loading) {
    return (
      <SimpleGrid h="100%" w="100%" spacing="xs" verticalSpacing="xs" cols={2}>
        <CampgroundImageSkeleton />
        <SimpleGrid cols={2} spacing="xs">
          <CampgroundImageSkeleton />
          <CampgroundImageSkeleton />
          {!isMediumScreen && (
            <>
              <CampgroundImageSkeleton />
              <CampgroundImageSkeleton />
            </>
          )}
        </SimpleGrid>
      </SimpleGrid>
    );
  }

  const layouts: Record<number, () => JSX.Element> = {
    1: () => <></>,
    2: () => <CampgroundImage onClick={() => openImage(1)} src={images[1]} />,
    3: () => (
      <Flex
        direction={{ base: "row", md: "column" }}
        gap={"xs"}
        flex={1}
        h="100%"
      >
        <CampgroundImage onClick={() => openImage(1)} src={images[1]} />
        <CampgroundImage onClick={() => openImage(2)} src={images[2]} />
      </Flex>
    ),
    4: () => (
      <Flex direction="column" gap={"xs"} flex={1} h="100%">
        <CampgroundImage onClick={() => openImage(1)} src={images[1]} />
        {!isMediumScreen && (
          <SimpleGrid cols={2} spacing="xs" h={"100%"}>
            <CampgroundImage onClick={() => openImage(2)} src={images[2]} />
            <CampgroundImage onClick={() => openImage(3)} src={images[3]} />
          </SimpleGrid>
        )}
      </Flex>
    ),
    5: () => (
      <SimpleGrid cols={2} spacing="xs">
        <CampgroundImage onClick={() => openImage(1)} src={images[1]} />
        <CampgroundImage onClick={() => openImage(2)} src={images[2]} />
        {!isMediumScreen && (
          <>
            <CampgroundImage onClick={() => openImage(3)} src={images[3]} />
            <CampgroundImage onClick={() => openImage(4)} src={images[4]} />
          </>
        )}
      </SimpleGrid>
    ),
  };

  const layout = layouts[Math.min(images.length, Object.keys(layouts).length)];

  return (
    <SimpleGrid
      h="100%"
      w="100%"
      spacing="xs"
      verticalSpacing="xs"
      cols={{ base: 1, md: images.length == 1 ? 1 : 2 }}
    >
      <CampgroundImage onClick={() => openImage(0)} src={images[0]} />
      {layout()}
    </SimpleGrid>
  );
};
